// The public door into the questionnaire: no link, an address proved by
// a six-digit code. The page never claims a code was sent - the server
// answers every address alike - and a proved address hands the session
// to the questionnaire page through the token, exactly as a link does.

import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import type { IntakeLanguage, IntakeLinkUiResponse } from "@duralex/shared";
import {
  fetchIntakeUi,
  IntakeLinkApiError,
  setIntakeLinkToken,
  startPublicIntake,
  verifyPublicIntake,
} from "../api/intakeLink";
import { Skeleton, SkeletonGroup } from "../../../components/chrome/Skeleton";
import { useIntakeLocale } from "../hooks/useIntakeLocale";
import { languageAutonym } from "../../../lib/intakeLanguages";

const FALLBACK: Record<string, string> = {
  error_title: "The page could not load",
  error_text: "Check your connection and try again in a minute.",
  error_retry: "Try again",
};

// Two codes in a row and only the newest works: the button waits.
const RESEND_COOLDOWN_MS = 30_000;

type Step = "email" | "code";

export default function IntakePublicStart() {
  const navigate = useNavigate();
  // No session at the door: the client's own choice, else the browser's
  // language, bounded by what the questionnaire speaks once /ui says.
  // The last answer that arrived is held: a switch keeps its words until
  // the new ones come, and for good if those never come.
  const [held, setHeld] = useState<IntakeLinkUiResponse | null>(null);
  const { locale, choose } = useIntakeLocale(null, held?.languages);
  const ui = useQuery({
    queryKey: ["intake-link", "ui", locale],
    queryFn: () => fetchIntakeUi(locale),
    staleTime: Infinity,
    retry: 1,
  });
  useEffect(() => {
    if (ui.data) setHeld(ui.data);
  }, [ui.data]);
  const served = ui.data ?? held;
  const spoken = served?.languages;
  const [step, setStep] = useState<Step>("email");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  // A field no person sees; a filled one is a bot and gets a quiet yes.
  const [website, setWebsite] = useState("");
  const [busy, setBusy] = useState(false);
  // The page's own words, or a server sentence (a burst limit) as it is.
  const [error, setError] = useState<string | null>(null);
  const [resendAt, setResendAt] = useState(0);
  // Focus follows the client back to the address field, never on first paint.
  const [returned, setReturned] = useState(false);
  const [now, setNow] = useState(() => Date.now());
  const codeField = useRef<HTMLInputElement>(null);
  const emailField = useRef<HTMLInputElement>(null);

  const uiMap = served?.ui;
  const t = (key: string): string => uiMap?.[key] ?? FALLBACK[key] ?? key;
  // The language the words came in, which the server may have narrowed
  // to English; English before any arrive.
  const spokenIn = served?.language ?? "en";

  useEffect(() => {
    if (step === "code") codeField.current?.focus();
    else if (returned) emailField.current?.focus();
  }, [step, returned]);

  // The cooldown counts down on screen only while it runs.
  useEffect(() => {
    if (now >= resendAt) return;
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [now, resendAt]);

  const failed = (err: unknown, code401: string | null) => {
    if (err instanceof IntakeLinkApiError) {
      if (err.status === 401 && code401) setError(code401);
      else if (err.status === 429) setError(err.message);
      else setError(t("error_text"));
    } else setError(t("error_text"));
  };

  const askForCode = async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await startPublicIntake({ email, website: website || undefined, language: locale });
      // Refused for everyone alike, never for this address: the firm's
      // mail is not proven, so no code exists to wait for.
      if (!r.ok) {
        setError(t("public_mail_unavailable"));
        return;
      }
      setResendAt(Date.now() + RESEND_COOLDOWN_MS);
      setNow(Date.now());
      setCode("");
      setStep("code");
      // A resend keeps the code form mounted and the step effect quiet, so
      // the field is focused here; the first move is the effect's.
      codeField.current?.focus();
    } catch (err) {
      failed(err, null);
    } finally {
      setBusy(false);
    }
  };

  const openWithCode = async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await verifyPublicIntake({ email, code, language: locale });
      setIntakeLinkToken(r.token);
      // A client-side move, never a reload: the token also lives only in
      // memory where the browser blocks site data.
      navigate("/intake/start", { replace: true });
    } catch (err) {
      failed(err, t("public_code_invalid"));
      setBusy(false);
      codeField.current?.select();
    }
  };

  // No words at all: the honest screen. A switch whose new words failed
  // keeps the old ones and the door stays open.
  if (ui.isError && !uiMap) {
    return (
      <main className="iform-note" lang={spokenIn}>
        <div className="iform-note-card">
          <h1>{t("error_title")}</h1>
          <p>{t("error_text")}</p>
          <button type="button" className="cc-btn-primary" onClick={() => void ui.refetch()}>
            {t("error_retry")}
          </button>
        </div>
      </main>
    );
  }
  if (!uiMap) {
    return (
      <main className="iform-note" lang={spokenIn}>
        <div className="iform-note-card">
          <SkeletonGroup label="Loading the page">
            <Skeleton width="60%" />
            <Skeleton width="85%" />
          </SkeletonGroup>
        </div>
      </main>
    );
  }

  const waiting = Math.max(0, Math.ceil((resendAt - now) / 1000));
  return (
    <main className="iform-note" lang={spokenIn}>
      <div className="iform-note-card">
        <h1>{t(step === "email" ? "public_title" : "public_code_title")}</h1>
        <p>{t(step === "email" ? "public_intro" : "public_code_text")}</p>
        {spoken && spoken.length > 1 && (
          <label className="cc-form-field">
            <span className="cc-form-label">{t("language")}</span>
            <select
              className="cc-field"
              aria-label={t("language")}
              value={locale}
              onChange={(e) => choose(e.target.value as IntakeLanguage)}
            >
              {spoken.map((l) => (
                <option key={l} value={l} lang={l}>
                  {languageAutonym(l)}
                </option>
              ))}
            </select>
          </label>
        )}
        {error && (
          <div role="alert" className="cc-form-error">
            {error}
          </div>
        )}
        {/* Keyed, or React would keep one <form> and retype its input in
            place between the steps - focus and value would carry over by
            accident of the tree shape, not by design. */}
        {step === "email" ? (
          <form
            key="email"
            onSubmit={(e) => {
              e.preventDefault();
              void askForCode();
            }}
          >
            <label className="cc-form-field">
              <span className="cc-form-label">{t("public_email")}</span>
              <input
                className="cc-field"
                type="email"
                name="email"
                autoComplete="email"
                required
                maxLength={200}
                ref={emailField}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </label>
            <input
              className="iform-honeypot"
              type="text"
              name="website"
              tabIndex={-1}
              aria-hidden="true"
              autoComplete="off"
              value={website}
              onChange={(e) => setWebsite(e.target.value)}
            />
            <div className="cc-form-actions">
              <button type="submit" className="cc-btn-primary" disabled={busy}>
                {busy ? t("submitting") : t("public_send")}
              </button>
            </div>
          </form>
        ) : (
          <form
            key="code"
            onSubmit={(e) => {
              e.preventDefault();
              void openWithCode();
            }}
          >
            <label className="cc-form-field">
              <span className="cc-form-label">{t("public_code")}</span>
              <input
                ref={codeField}
                className="cc-field"
                type="text"
                name="code"
                inputMode="numeric"
                autoComplete="one-time-code"
                pattern="[0-9]{6}"
                maxLength={6}
                required
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
              />
            </label>
            <div className="cc-form-actions">
              <button type="submit" className="cc-btn-primary" disabled={busy}>
                {busy ? t("submitting") : t("public_open")}
              </button>
            </div>
            <p>
              <button
                type="button"
                className="cc-link-btn"
                disabled={busy || waiting > 0}
                onClick={() => void askForCode()}
              >
                {t("public_resend")}
                {waiting > 0 ? ` (${waiting})` : ""}
              </button>
              {waiting > 0 && <span className="cc-form-hint">{t("public_resend_hint")}</span>}
            </p>
            <p>
              <button
                type="button"
                className="cc-link-btn"
                disabled={busy}
                onClick={() => {
                  setStep("email");
                  setError(null);
                  setReturned(true);
                }}
              >
                {t("public_change_email")}
              </button>
            </p>
          </form>
        )}
      </div>
    </main>
  );
}
